import type {
  BaseCartLineConnection,
  BaseCartLineEdge,
  Cart,
  CartAttributesUpdatePayload,
  CartBuyerIdentityUpdatePayload,
  CartCreatePayload,
  CartDiscountCodesUpdatePayload,
  CartLine,
  CartLinesAddPayload,
  CartLinesRemovePayload,
  CartLinesUpdatePayload,
  ComponentizableCartLine,
  Product,
  ProductVariant,
} from "@shopify/hydrogen-react/storefront-api-types";
import { mutation, query } from "gql-query-builder";
import { attributeFields, cartErrorFields, customerFields, moneyFields } from "./fields";
import {
  availableCountriesMetafield,
  brandNameMetafield,
  brandVariantIdMetafield,
  noticeMetafield,
  quantityLimitMetafield,
  quantityLimitProductMetafield,
  saleMetafield,
  thumbnailImagesMetafield,
  unavailableCountriesMetafield,
} from "./metafields/product-metafields";
import type { IProductMetafields, IProductVariantMetafields } from "./product-gql";
import {
  cartAutomaticDiscountAllocationFragment,
  cartCodeDiscountAllocationFragment,
  cartCustomDiscountAllocationFragment,
  productVariantFragment,
} from "./fragments";

export const deliveryGroupFields = [
  "id",
  { deliveryOptions: ["title", "deliveryMethodType", { estimatedCost: ["amount"] }] },
];

export const lines = {
  operation: "lines(first: 50)",
  fields: [
    {
      edges: [
        {
          node: [
            "id",
            "quantity",
            { attributes: ["key", "value"] },
            {
              cost: [
                { amountPerQuantity: moneyFields },
                { compareAtAmountPerQuantity: moneyFields },
                { subtotalAmount: moneyFields },
                { totalAmount: moneyFields },
              ],
            },
            {
              discountAllocations: [
                cartCodeDiscountAllocationFragment,
                cartCustomDiscountAllocationFragment,
                cartAutomaticDiscountAllocationFragment,
              ],
            },
            {
              merchandise: [
                {
                  ...productVariantFragment,
                  fields: [
                    brandVariantIdMetafield,
                    ...productVariantFragment.fields.map((item) =>
                      typeof item === "object" && item.product
                        ? {
                            product: [
                              ...item.product,
                              quantityLimitMetafield,
                              quantityLimitProductMetafield,
                              noticeMetafield,
                              availableCountriesMetafield,
                              unavailableCountriesMetafield,
                              saleMetafield,
                              brandNameMetafield,
                              thumbnailImagesMetafield,
                            ],
                          }
                        : item,
                    ),
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  variables: {},
};

export const cartFields = [
  "id",
  "totalQuantity",
  { attributes: attributeFields },
  {
    buyerIdentity: ["countryCode", "email", "phone", { customer: customerFields }],
  },
  {
    discountAllocations: [
      cartCustomDiscountAllocationFragment,
      cartCodeDiscountAllocationFragment,
      cartAutomaticDiscountAllocationFragment,
    ],
  },
  { discountCodes: ["applicable", "code"] },
  {
    cost: [
      { checkoutChargeAmount: moneyFields },
      { subtotalAmount: moneyFields },
      { totalAmount: moneyFields },
      { totalDutyAmount: moneyFields },
      { totalTaxAmount: moneyFields },
    ],
  },
  "checkoutUrl",
  "createdAt",
  "note",
  "totalQuantity",
  "updatedAt",
  lines,
  {
    operation: "deliveryGroups(first: 50)",
    fields: [{ edges: [{ node: deliveryGroupFields }] }],
    variables: {},
  },
];

export interface ICartEdge extends BaseCartLineEdge {
  node: (CartLine | ComponentizableCartLine) & {
    merchandise: ProductVariant &
      IProductVariantMetafields & {
        product: Product & IProductMetafields;
      };
  };
}

/**
 * 카트 정보 조회
 */
// TODO: Omit으로 교차 타입 해소 → ICart["cart"]["lines"]["edges"] 가 ICartEdge[]로 추론되도록 수정
// export interface ICart {
//   cart: Omit<Cart, "lines"> & {
//     lines: Omit<BaseCartLineConnection, "edges"> & {
//       edges: ICartEdge[];
//     };
//   };
// }
export interface ICart {
  cart: Cart & {
    lines: BaseCartLineConnection & {
      edges: ICartEdge[];
    };
  };
}

export const cartQuery = query(
  {
    operation: "cart",
    fields: cartFields,
    variables: { id: { type: "ID!" } },
  },
  undefined,
  { operationName: "Cart" },
);

export const cartDeferQuery = query(
  {
    operation: "cart",
    fields: [
      "id",
      { buyerIdentity: [{ customer: ["id"] }] },
      {
        operation: "Cart @defer",
        fields: [
          {
            operation: "deliveryGroups(first: 3, withCarrierRates: true)",
            fields: [{ edges: [{ node: deliveryGroupFields }] }],
            variables: {},
          },
        ],
        fragment: true,
        operationName: "DeliveryGroups",
      },
    ],
    variables: { id: { type: "ID!" } },
  },
  undefined,
  { operationName: "CartWithDefer" },
);

/**
 * 카트 속성 업데이트
 */
export interface ICartAttributesUpdateMutation {
  cartAttributesUpdate: CartAttributesUpdatePayload & ICart;
}

export const cartAttributesUpdateMutation = mutation(
  {
    operation: "cartAttributesUpdate",
    fields: [{ cart: cartFields }, { userErrors: cartErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      attributes: { type: "[AttributeInput!]!" },
    },
  },
  undefined,
  { operationName: "CartAttributesUpdate" },
);

/**
 * 카트 구매자 정보 업데이트
 */
export interface ICartBuyerIdentityUpdateMutation {
  cartBuyerIdentityUpdate: CartBuyerIdentityUpdatePayload & ICart;
}

export const cartBuyerIdentityUpdateMutation = mutation(
  {
    operation: "cartBuyerIdentityUpdate",
    fields: [{ cart: cartFields }, { userErrors: cartErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      buyerIdentity: { type: "CartBuyerIdentityInput!" },
    },
  },
  undefined,
  { operationName: "CartBuyerIdentityUpdate" },
);

/**
 * 카드 생성
 */
export interface ICartCreateMutation {
  cartCreate: CartCreatePayload & ICart;
}

export const cartCreateMutation = mutation(
  {
    operation: "cartCreate",
    fields: [{ cart: cartFields }, { userErrors: cartErrorFields }],
  },
  undefined,
  { operationName: "CartCreate" },
);

/**
 * 할인코드 제어
 */
export interface ICartDiscountCodesUpdateMutation {
  cartDiscountCodesUpdate: CartDiscountCodesUpdatePayload & ICart;
}

export const cartDiscountCodesUpdateMutation = mutation(
  {
    operation: "cartDiscountCodesUpdate",
    fields: [{ cart: cartFields }, { userErrors: cartErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      discountCodes: { type: "[String!]" },
    },
  },
  undefined,
  { operationName: "CartDiscountCodesUpdate" },
);

/**
 * 카트 아이템 추가
 */
export interface ICartLinesAddMutation {
  cartLinesAdd: CartLinesAddPayload & ICart;
}

export const cartLinesAddMutation = mutation(
  {
    operation: "cartLinesAdd",
    fields: [{ cart: cartFields }, { userErrors: cartErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      lines: { type: "[CartLineInput!]!" },
    },
  },
  undefined,
  { operationName: "CartLinesAdd" },
);

/**
 * 카트 아이템 제거
 */
export interface ICartLinesRemoveMutation {
  cartLinesRemove: CartLinesRemovePayload & ICart;
}

export const cartLinesRemoveMutation = mutation(
  {
    operation: "cartLinesRemove",
    fields: [{ cart: cartFields }, { userErrors: cartErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      lineIds: { type: "[ID!]!" },
    },
  },
  undefined,
  { operationName: "CartLinesRemove" },
);

/**
 * 카트 아이템 수량 업데이트
 */
export interface ICartLinesUpdateMutation {
  cartLinesUpdate: CartLinesUpdatePayload & ICart;
}

export const cartLinesUpdateMutation = mutation(
  {
    operation: "cartLinesUpdate",
    fields: [{ cart: cartFields }, { userErrors: cartErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      lines: { type: "[CartLineUpdateInput!]!" },
    },
  },
  undefined,
  { operationName: "CartLinesUpdate" },
);

/**
 * 카드 추가
 */
export interface ICartCreateInputMutation {
  cartCreate: CartCreatePayload & ICart;
}

export const cartCreateInputMutation = mutation(
  {
    operation: "cartCreate",
    fields: [{ cart: cartFields }, { userErrors: cartErrorFields }],
    variables: {
      input: { type: "CartInput!" },
    },
  },
  undefined,
  { operationName: "CartCreateInput" },
);
